import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaHeart, FaUsers, FaCalendarAlt, FaComments, FaImages, 
  FaHistory, FaArrowRight, FaArrowLeft, FaTimes, FaLightbulb,
  FaQuoteLeft, FaHandsHelping, FaBell
} from 'react-icons/fa';

// Inspirational quotes for members
const MEMBER_QUOTES = [
  {
    quote: "The family is one of nature's masterpieces.",
    author: "George Santayana",
    title: "Philosopher"
  },
  {
    quote: "Family is not an important thing. It's everything.",
    author: "Michael J. Fox",
    title: "Actor & Advocate"
  },
  {
    quote: "In family life, love is the oil that eases friction.",
    author: "Friedrich Nietzsche",
    title: "Philosopher"
  },
  {
    quote: "The love of family and the admiration of friends is much more important than wealth and privilege.",
    author: "Charles Kuralt",
    title: "Journalist"
  }
];

// Member-specific journey steps - shorter and focused on participation
const MEMBER_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Your Family!',
    icon: FaHeart,
    content: {
      headline: "You've Been Invited to Something Special",
      description: "Welcome to Legacy Forge Family! Someone in your family thought of you and invited you to join this private space where your family connects, shares memories, and builds lasting bonds.",
      quoteIndex: 0,
      tip: "This is your family's private space — only invited members can see what's shared here."
    }
  },
  {
    id: 'explore',
    title: 'Explore Your Family Hub',
    icon: FaUsers,
    content: {
      headline: "Your Family's Digital Home",
      description: "This is where your family comes together. You'll find tabs for different activities — events, chat, photos, and more. Take your time exploring!",
      features: [
        { icon: FaCalendarAlt, name: "Events", desc: "See upcoming family gatherings and reunions" },
        { icon: FaComments, name: "Chat", desc: "Stay connected with family messages" },
        { icon: FaImages, name: "Gallery", desc: "View and share family photos" }
      ]
    }
  },
  {
    id: 'participate',
    title: 'How You Can Participate',
    icon: FaHandsHelping,
    content: {
      headline: "Your Role Matters",
      description: "Every family member brings something unique. Here's how you can contribute to your family's story:",
      tips: [
        "Share photos from family events and everyday moments",
        "Join conversations in the family chat",
        "RSVP to events and reunions",
        "Add to the family timeline with memories and milestones",
        "Support and encourage other family members"
      ]
    }
  },
  {
    id: 'stay-connected',
    title: 'Stay Connected',
    icon: FaBell,
    content: {
      headline: "Never Miss a Family Moment",
      description: "Legacy Forge will keep you updated on what's happening in your family. You'll receive notifications for:",
      features: [
        { icon: FaCalendarAlt, name: "Event Invites", desc: "When new family events are planned" },
        { icon: FaComments, name: "Messages", desc: "When family members send messages" },
        { icon: FaImages, name: "New Photos", desc: "When photos are added to the gallery" }
      ],
      tip: "Check in regularly to stay connected with your family — even a quick message can brighten someone's day!"
    }
  },
  {
    id: 'completion',
    title: "You're All Set!",
    icon: FaLightbulb,
    content: {
      headline: "Welcome to the Family",
      description: "You're now ready to explore and participate in your family's journey. Remember:",
      tips: [
        "This is a safe, private space for your family",
        "Share memories and moments that matter",
        "Stay connected even when you're far apart",
        "Your participation makes the family stronger"
      ],
      closing: "Thank you for being part of this family. Your presence here matters.",
      quoteIndex: 1
    }
  }
];

// Member Welcome Tour Card
function MemberTourCard({ step, currentIndex, totalSteps, onNext, onPrev, onSkip }) {
  const Icon = step.icon;
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === totalSteps - 1;
  const quote = step.content.quoteIndex !== undefined ? MEMBER_QUOTES[step.content.quoteIndex] : null;

  return (
    <div 
      className="fixed z-[9999] w-[90vw] max-w-[450px] bg-white rounded-2xl shadow-2xl overflow-hidden"
      style={{
        top: '45%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: 'calc(85vh - 60px)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Icon className="text-white text-lg" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">{step.title}</h3>
              <p className="text-emerald-100 text-sm">Step {currentIndex + 1} of {totalSteps}</p>
            </div>
          </div>
          <button
            onClick={onSkip}
            className="text-white/70 hover:text-white transition p-2"
            title="Skip tour"
          >
            <FaTimes />
          </button>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white transition-all duration-500"
            style={{ width: `${((currentIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5 overflow-y-auto flex-1">
        <h4 className="text-xl font-bold text-gray-900 mb-3">
          {step.content.headline}
        </h4>
        
        <p className="text-gray-700 mb-4 leading-relaxed">
          {step.content.description}
        </p>

        {/* Quote */}
        {quote && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 p-4 mb-4 rounded-r-lg">
            <FaQuoteLeft className="text-emerald-500 mb-2" />
            <p className="text-gray-800 italic leading-relaxed">"{quote.quote}"</p>
            <p className="text-emerald-700 font-semibold text-sm mt-2">— {quote.author}</p>
          </div>
        )}

        {/* Features list */}
        {step.content.features && (
          <div className="space-y-2 mb-4">
            {step.content.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <feature.icon className="text-emerald-600 text-lg" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{feature.name}</p>
                  <p className="text-gray-600 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tips list */}
        {step.content.tips && (
          <div className="space-y-2 mb-4">
            {step.content.tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-emerald-500 mt-1">✓</span>
                <p className="text-gray-700 text-sm">{tip}</p>
              </div>
            ))}
          </div>
        )}

        {/* Single tip */}
        {step.content.tip && (
          <div className="flex items-start gap-2 text-sm bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <FaLightbulb className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-gray-700">{step.content.tip}</p>
          </div>
        )}

        {/* Closing message */}
        {step.content.closing && (
          <p className="text-gray-800 font-medium mt-4 text-center italic">
            {step.content.closing}
          </p>
        )}
      </div>

      {/* Footer - with mobile spacing */}
      <div className="px-6 py-4 pb-6 sm:pb-4 bg-gray-50 border-t flex items-center justify-between flex-shrink-0 mb-16 sm:mb-0">
        <button
          onClick={onPrev}
          disabled={isFirstStep}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
            isFirstStep 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <FaArrowLeft />
          <span>Back</span>
        </button>

        <button
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium"
        >
          <span>{isLastStep ? "Get Started!" : "Next"}</span>
          {!isLastStep && <FaArrowRight />}
        </button>
      </div>
    </div>
  );
}

// Main Member Welcome Tour component
export function MemberWelcomeTour({ isOpen, onClose, familyId }) {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Reset when tour opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStepIndex(0);
    }
  }, [isOpen]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < MEMBER_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Tour complete
      localStorage.setItem('memberWelcomeTourCompleted', 'true');
      onClose();
      // Navigate to family page if we have the ID
      if (familyId) {
        navigate(`/family/${familyId}`);
      }
    }
  }, [currentStepIndex, onClose, navigate, familyId]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('memberWelcomeTourSkipped', 'true');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const currentStep = MEMBER_STEPS[currentStepIndex];

  return (
    <div className="member-welcome-tour">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={handleSkip} />
      
      {/* Tour Card */}
      <MemberTourCard
        step={currentStep}
        currentIndex={currentStepIndex}
        totalSteps={MEMBER_STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
      />
    </div>
  );
}

// Hook to check if member should see welcome tour
export function useMemberWelcomeTour() {
  const [showTour, setShowTour] = useState(false);

  const checkAndShowTour = useCallback((isNewMember = false) => {
    const completed = localStorage.getItem('memberWelcomeTourCompleted');
    const skipped = localStorage.getItem('memberWelcomeTourSkipped');
    
    if (isNewMember && !completed && !skipped) {
      setShowTour(true);
    }
  }, []);

  const closeTour = useCallback(() => {
    setShowTour(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem('memberWelcomeTourCompleted');
    localStorage.removeItem('memberWelcomeTourSkipped');
  }, []);

  return { showTour, checkAndShowTour, closeTour, resetTour, setShowTour };
}

export default MemberWelcomeTour;
